import { UpgradeCta } from './UpgradeCta.js';
import type { SubscriptionTier } from '../api/subscriptions.js';

/**
 * The friendly face of a plan gate. A 402 PLAN_REQUIRED is an upsell, not an
 * error — so it renders as a calm indigo (brand) card, never alarm-red. Fed by
 * <ApiErrorNotice> from the API body's requiredTier + missingFeature; also
 * usable standalone to pre-empt a gate in the UI.
 *
 * The CTA reuses <UpgradeCta> and routes by the feature's REQUIRED tier: a
 * self-serve tier (Starter/Growth) → "View plans" → /billing; Enterprise is
 * sales-led → "Contact sales" → the contact modal (not /billing, which can't
 * sell Enterprise).
 */

const TIER_LABEL: Record<SubscriptionTier, string> = {
  starter: 'Starter',
  growth: 'Growth',
  enterprise: 'Enterprise',
};

/** "asset_management" → "Asset management". Falls back to the raw key. */
function humanizeFeature(key?: string): string | null {
  if (!key) return null;
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  if (!spaced) return null;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function UpgradePrompt({
  requiredTier,
  missingFeature,
  className,
}: {
  /** The tier the feature needs (from the 402 body). */
  requiredTier?: SubscriptionTier | null;
  /** The entitlement key (from the 402 body), shown humanized when present. */
  missingFeature?: string;
  className?: string;
}): JSX.Element {
  const tierName = requiredTier ? TIER_LABEL[requiredTier] : null;
  // Enterprise is sales-led (not self-serve) — route its CTA to Contact sales,
  // not the Growth self-serve /billing checkout.
  const salesLed = requiredTier === 'enterprise';
  const feature = humanizeFeature(missingFeature);
  const title = tierName ? `${tierName} plan feature` : 'Upgrade required';
  const body = feature
    ? tierName
      ? `${feature} is available on the ${tierName} plan.`
      : `${feature} isn't included in your current plan.`
    : tierName
      ? `This feature is available on the ${tierName} plan.`
      : `This feature isn't included in your current plan.`;

  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-indigo-primary/40 bg-indigo-primary/10 px-4 py-3 ${className ?? ''}`}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5 shrink-0 text-indigo-primary"
      >
        {/* upward chevrons — "upgrade" */}
        <path d="m17 11-5-5-5 5M17 18l-5-5-5 5" />
      </svg>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium text-white">{title}</p>
        <p className="text-sm text-gray-300">{body}</p>
      </div>
      <UpgradeCta
        salesLed={salesLed}
        label={salesLed ? 'Contact sales' : 'View plans'}
        className="shrink-0 rounded bg-indigo-primary px-3 py-2 text-sm font-medium text-white hover:opacity-90"
      />
    </div>
  );
}
